// Types for the universal sidebar route system
export type TSidebarRouteConfig = {
  path: string;
  aiEngine?: string;
  requiresSuperuser?: boolean;
  requiresEntity?: boolean;
  dynamicPath?: (entityId: string) => string;
};

export type TUserContext = {
  featureShowHideFlag?: any;
  isSuperuser?: boolean;
  entity?: any;
};

export const structureObj = (array: string[]) => {
  return array?.map((item: string) => {
    return { path: `/${item}`, aiEngine: item };
  });
};

/**
 * Employee management routes extracted from CommonSidebar.tsx
 * These routes match exactly what's defined in the sidebar component
 */

export const EMPLOYEE_MANAGEMENT_ROUTES_PATH = ["employee-management"];

/**
 * Nano Learning routes extracted from CommonSidebar.tsx
 * These routes match exactly what's defined in the sidebar component
 */

export const NANO_LEARNING_ROUTES_PATH = [
  "learning",
  "nano-learning",
  "create-nano-learning",
  "edit-nano-learning",
  "learning",
];

/**
 * Analytics routes — used to keep the sidebar link active across all analytics sub-pages
 */
export const ANALYTICS_ROUTES_PATH = ["analytics"];

/**
 * Reports routes extracted from CommonSidebar.tsx
 */
export const REPORTS_ROUTES_PATH = [
  "reports",
  "violations-by-category",
  "agent-performance-list",
  "personalized-coaching-insights",
  "customer-insights",
  "sentiment-analysis-report",
  "team-wise-report",
  "campaigns-wise-team-report",
  "agent-performance-details",
  "ai-improvment-tracking-details",
];

/**
 * Agent Dashboard routes extracted from CommonSidebar.tsx
 */
export const AGENT_DASHBOARD_ROUTES_PATH = [
  "agent-performance-details",
  "ai-improvment-tracking-details",
];

/**
 * EvalutionList routes extracted from CommonSidebar.tsx
 */
export const EVALUTION_LIST_ROUTES_PATH = [
  "evaluation-listing",
  "evaluation-details",
];

/**
 * Manage Template routes extracted from CommonSidebar.tsx
 */
export const MANAGE_TEMPLATE_ROUTES_PATH = [
  "manage-template",
  "add-template",
  "edit-template",
];

/**
 * Reports routes extracted from CommonSidebar.tsx
 */
export const CONFIGURATION_ROUTES_PATH = [
  "configurations",
  "call-processing-queue-planner",
  "agent-ranking-configuration",
  "create-entity",
  "branding",
  "entity-scale-rating",
  "employee-attributes",
  "role-management",
  "automation-rule-management",
  "organization-hierarchy",
  "selection-criteria",
  "campaign",
  "services",
  "data-ingestion",
  "add-data-ingestion-configuration",
  "edit-data-ingestion-configuration",
  "push-api-clients",
];

/**
 * Admin Configuration routes extracted from CommonSidebar.tsx
 */
export const ADMIN_CONFIGURATIONS_ROUTES_PATH = [
  "admin",
  "admin/dashboard",
  "admin/workflow-builder",
  "admin/entity-field-mapping",
  "admin/campaigns",
  "admin/services",
  "admin/call-summary",
  "admin/stt-engine-configuration",
  "admin/domains",
  "admin/ai-engine-configurations",
  "admin/date-time-settings",
];

/**
 * Universal function that0 works with any sidebar section
 * Just pass the route configuration array and user context
 *
 * @param routes - Route configuration array (e.g., CONFIGURATION_ROUTES, etc.)
 * @param featureShowHideFlag - Feature flags from the store
 * @param isSuperuser - Whether the user is a superuser
 * @param entity - Entity information
 * @param fallbackPath - Fallback path if no routes are available
 * @returns The first available route path
 */
export const getFirstAvailableSidebarRoute = (
  routes: TSidebarRouteConfig[],
  featureShowHideFlag: string[] | null | undefined,
  isSuperuser: boolean,
  entity: { internal_id?: string } | null | undefined,
  fallbackPath?: string,
): string => {
  for (const route of routes) {
    // Check ai_engine permission
    if (
      route.aiEngine &&
      !featureShowHideFlag?.some((flag) => flag === route?.aiEngine)
    ) {
      continue;
    }

    // Check superuser requirement
    if (route.requiresSuperuser && !isSuperuser) {
      continue;
    }

    // Check entity requirement
    if (route.requiresEntity && !entity?.internal_id) {
      continue;
    }

    // If we reach here, this route is available
    // Use dynamic path if provided, otherwise use static path
    if (route.dynamicPath && entity?.internal_id) {
      return route.dynamicPath(entity.internal_id);
    }

    return route.path;
  }

  // Return fallback path or empty string if no routes are available
  return fallbackPath ?? "404";
};
